import Dexie, { Table } from 'dexie';

export type CardType = 'speaking' | 'writing' | 'listening' | 'vocab';
export type CardMode = 'relearn' | 'review' | 'topic-recall' | 'topic-dictation' | 'translation' | 'argument';
export type SrsFeedback = 'again' | 'hard' | 'good';

export interface SpeakingMeta {
  prompt?: string;
  question?: string;
  topic?: string;
  source?: string;
  draftRecordingKey?: string;
  finalRecordingKey?: string;
  transcript?: string;
  selfReview?: string;
  bestAnswerSummary?: string;
  extraMeta?: Record<string, unknown>;
}

export interface WritingCloze {
  template: string; // e.g. "I {{blank_0}} to school."
  answers: string[]; // expected phrases for each blank, aligned by index
  sourceId?: string; // base paragraph card id
  reference?: string; // original full sentence/paragraph
  generatedAt?: number;
  occurrence?: number; // how many times this cloze was regenerated
}

export interface WritingMeta {
  cnHint?: string;
  enOriginal?: string;
  cnTranslation?: string; // 英文原文的中文翻译
  keyPhrases?: string[];
  keyPoints?: string[]; // AI 写作卡片的关键词/要点
  topics?: string[];
  topicTag?: string;
  bestVersion?: string;
  synonymWhitelistId?: string;
  extraMeta?: Record<string, unknown>;
  lastRecall?: ParagraphRecallRecord;
  cloze?: WritingCloze;
}

export interface DiffRange {
  text: string;
  kind: 'hit' | 'missing' | 'extra' | 'typo';
  /** 用户实际写的词（仅 typo 类型时有值） */
  actual?: string;
}

export interface LocalParagraphScore {
  coverage: number; // 0-100
  missingUnits: string[];
  weakSentences: string[];
  diffRanges: DiffRange[];
  synonymHits: string[];
}

export interface ParagraphLLMFeedback {
  improvedAnswer: string;
  suggestions: string[];
  provider?: string;
  latencyMs?: number;
}

export interface ParagraphRecallRecord {
  answer: string;
  evaluatedAt: number;
  localScore: LocalParagraphScore;
  llmFeedback?: ParagraphLLMFeedback;
}

export interface ListeningMeta {
  materialId?: string;
  sourceId?: string;
  sentenceId?: string | number;
  audioPath?: string;
  audioKey?: string;
  start?: number;
  end?: number;
  transcript?: string;
  clozeTemplate?: string;
  clozeIndexes?: number[];
  clozeAnswers?: string[];
  initialMode?: CardMode;
  audioMissing?: boolean;
  extraMeta?: Record<string, unknown>;
}

export interface VocabMeta {
  en?: string;
  cn?: string;
  scene?: string;
  examples?: string[];
  acceptableAnswers?: string[];
  important?: boolean;
  front?: string;
  back?: string;
  exampleSentence?: string;
  scenarioTopics?: string[];
  extraMeta?: ExtraMeta;
}

export interface CardHistoryEntry {
  ts: number;
  rating: SrsFeedback;
  mode: CardMode;
  interval: number;
  ease: number;
  nextReviewDate: number;
  answerMeta?: Record<string, unknown>;
  notes?: string;
}

// ============= Content 类型定义 =============

/** 听力卡片的 segment 结构 */
export interface ListeningSegment {
  english?: string;
  chinese?: string;
  startMs?: number;
  start?: number;
  endMs?: number;
  end?: number;
  durationMs?: number;
}

/** 听力卡片的 content 结构 */
export interface ListeningContent {
  segment?: ListeningSegment;
  audioPath?: string;
  audioUrl?: string;
  fallbackAudioUrl?: string;
  audioMissing?: boolean;
  meta?: {
    title?: string;
    audioUrl?: string;
  };
}

/** 写作卡片的 content 结构 */
export interface WritingContent {
  prompt?: string;
  answer?: string;
  reference?: string;
  clozeTemplate?: string;
  clozeAnswers?: string[];
  sourceCardId?: string;
  generatedAt?: number;
  topicTag?: string;
  csvTopic?: string;
  keyPhrases?: string[];
  lastRecallAnswer?: string;
}

/** 词汇卡片的 content 结构 */
export interface VocabContent {
  term?: string;
  definition?: string;
  example?: string;
  examples?: string[];
  scene?: string;
  acceptableAnswers?: string[];
  occurrence?: number;
  generatedAt?: number;
}

/** 口语卡片的 content 结构 */
export interface SpeakingContent {
  topic?: string;
  question?: string;
}

/** 通用 extraMeta 结构 */
export interface ExtraMeta {
  scene?: string;
  important?: boolean;
  occurrence?: number;
  sourceForCloze?: string;
  from?: string;
  lightReviewRecordingKey?: string;
  latestFeedback?: Record<string, unknown>;
  thinkingHints?: Record<string, unknown>;
  [key: string]: unknown;
}

/** 卡片 content 联合类型 */
export type CardContent = ListeningContent | WritingContent | VocabContent | SpeakingContent | Record<string, unknown>;

export interface CardRecord {
  id: string;
  type: CardType;
  mode: CardMode;
  topics?: string[];
  tags?: string[];
  unitId?: string;
  sourceId?: string;
  ease: number;
  interval: number;
  nextReviewDate: number;
  consecutiveGood: number;
  isRelearn?: boolean;
  relearnBucket?: number;
  priority?: number;
  isSoftDeleted?: boolean;
  contentId?: string;
  contentVersion?: string;
  contentHash?: string;
  audioKey?: string;
  audioUrl?: string;
  audioBlobRef?: string;
  content?: CardContent;
  speakingMeta?: SpeakingMeta;
  writingMeta?: WritingMeta;
  listeningMeta?: ListeningMeta;
  vocabMeta?: VocabMeta;
  history: CardHistoryEntry[];
  createdAt: number;
  updatedAt: number;
}

export interface SrsPreferences {
  // 每日时间目标（分钟）
  dailyTimeGoal?: number;

  // 每种卡片的预估时间（分钟）
  cardTimeEstimates?: Partial<Record<CardType, number>>;

  // 新卡片引入速率（每天）
  newCardsPerDay?: number;

  // 口语 Part 比例
  speakingPartRatio?: { part1: number; part2: number; part3: number };

  // SRS 算法调整
  easeAdjustment?: number; // -0.5 ~ 0.5
}

/** 学习目标规划 */
export interface StudyGoal {
  targetDate: number;            // 目标完成日期时间戳
  createdAt: number;
  updatedAt: number;

  // 学习范围
  scope: {
    speaking: {
      enabled: boolean;
      parts: ('Part 1' | 'Part 2' | 'Part 3')[];
      ratio?: number;            // 学习比例 0-100%，默认 100
    };
    writing: {
      enabled: boolean;
      modes: CardMode[];         // ['translation'] = 个人
      ratio?: number;            // 学习比例 0-100%，默认 100
    };
    listening: { enabled: boolean };
    vocab: { enabled: boolean };
  };

  // 时间估算配置（分钟）
  timeEstimates: {
    speakingPart1: number;       // 默认 3
    speakingPart2: number;       // 默认 7
    speakingPart3: number;       // 默认 3
    writing: number;             // 默认 4
    listening: number;           // 默认 2
    vocab: number;               // 默认 1
  };

  // 复习设置
  reviewSettings: {
    includeReview: boolean;      // 是否计算复习时间
    // 按类型设置
    speaking: {
      reviewTimes: number;       // 口语复习次数，默认 2
      reviewRatio: number;       // 口语复习比例，默认 0.5
    };
    writing: {
      reviewTimes: number;       // 写作复习次数，默认 4
      reviewRatio: number;       // 写作复习比例，默认 0.5
    };
  };

  // 口语 Part 抽取比例
  speakingPartRatio: {
    part1: number;               // 默认 2
    part2: number;               // 默认 1
    part3: number;               // 默认 1
  };
}

/** 周学习目标 */
export interface WeeklyGoal {
  id: string;

  // 目标设定
  targetHoursPerWeek: number;    // 每周目标时长（小时）

  // 可选：各技能分配（百分比，总和应为 100）
  skillAllocation?: {
    speaking?: number;
    writing?: number;
    listening?: number;
    vocab?: number;
  };

  // 目标分数（用于 AI 评价参考）
  targetBand?: number;           // 如 7.0

  createdAt: number;
  updatedAt: number;
}

/** 学习记录（每次练习结束时写入） */
export interface StudySessionLog {
  id: string;
  cardId?: string;
  cardType: CardType;
  mode: CardMode;

  // 时间记录
  startedAt: number;
  endedAt: number;
  durationMs: number;

  // 结果
  feedback?: SrsFeedback;
  score?: number;
}

/** 周统计快照（每周生成） */
export interface WeeklyStats {
  weekStart: number;             // 周一 00:00 时间戳
  weekEnd: number;               // 周日 23:59 时间戳

  // 学习时长（分钟）
  totalMinutes: number;
  bySkill: Record<CardType, number>;

  // 学习量
  cardsCompleted: number;
  newCardsLearned: number;

  // 目标完成率
  goalHours: number;
  actualHours: number;
  completionRate: number;        // 0-100%

  // 趋势
  comparedToLastWeek?: number;   // +/-百分比
}

export interface SettingsRecord {
  id: string;
  dailyLimits: Partial<Record<CardType, number>>;

  // SRS 偏好设置
  srsPreferences?: SrsPreferences;

  // 学习目标（旧版，保留兼容）
  studyGoal?: StudyGoal;

  // 周学习目标（新版）
  weeklyGoal?: WeeklyGoal;

  preferences?: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export interface TodaySnapshot {
  id: string;
  generatedAt: number;
  totals: {
    dueCount: number;
    plannedCount: number;
    overflowCount: number;
  };
  skills: Array<{
    type: CardType;
    dueCount: number;
    plannedCount: number;
    overflowCount: number;
    limit: number;
  }>;
  note?: string;
}

export type NewCardRecord = Partial<CardRecord> &
  Pick<CardRecord, 'type'> & {
    id?: string;
    mode?: CardMode;
    nextReviewDate?: number;
    ease?: number;
    interval?: number;
    consecutiveGood?: number;
    history?: CardHistoryEntry[];
  };

const DEFAULT_DB_NAME = 'enpass-db';
export const DEFAULT_EASE = 2.5;
export const DEFAULT_INTERVAL = 0;
export const DEFAULT_GOOD_STREAK = 0;
export const TODAY_SNAPSHOT_ID = 'today-latest';

const nowTs = () => Date.now();

export class MyIELTSDatabase extends Dexie {
  cards!: Table<CardRecord, string>;
  settings!: Table<SettingsRecord, string>;
  snapshots!: Table<TodaySnapshot, string>;
  studyLogs!: Table<StudySessionLog, string>;

  constructor(name: string = DEFAULT_DB_NAME) {
    super(name);

    this.version(1).stores({
      cards: '&id, type, nextReviewDate, [type+nextReviewDate]',
      settings: '&id',
    });

    this.version(2)
      .stores({
        cards: '&id, type, nextReviewDate, [type+nextReviewDate]',
        settings: '&id',
      })
      .upgrade(async (tx) => {
        await tx
          .table('cards')
          .toCollection()
          .modify((card: Partial<CardRecord>) => {
            card.interval = (card as any).intervalDays ?? card.interval ?? DEFAULT_INTERVAL;
            delete (card as any).intervalDays;
            card.ease = (card as any).easeFactor ?? card.ease ?? DEFAULT_EASE;
            delete (card as any).easeFactor;
            card.consecutiveGood = card.consecutiveGood ?? DEFAULT_GOOD_STREAK;
            // Default mode mapping
            const srsStatus = (card as any).srsStatus as string | undefined;
            if (!card.mode) {
              card.mode = srsStatus === 'learning' || srsStatus === 'new' ? 'relearn' : 'review';
            }
          });
      });

    this.version(3)
      .stores({
        cards:
          '&id, contentId, [contentId+contentVersion], contentHash, type, mode, nextReviewDate, [type+mode+nextReviewDate], [topics+nextReviewDate], [unitId+nextReviewDate], [isRelearn+relearnBucket+nextReviewDate], isSoftDeleted, *topics, *tags',
        settings: '&id',
        snapshots: '&id, generatedAt',
      })
      .upgrade(async (tx) => {
        await tx.table('cards').toCollection().modify((card: Partial<CardRecord>) => {
          card.mode = card.mode ?? 'review';
          card.consecutiveGood = card.consecutiveGood ?? DEFAULT_GOOD_STREAK;
          card.ease = card.ease ?? DEFAULT_EASE;
          card.interval = card.interval ?? DEFAULT_INTERVAL;
        });
      });

    // v4: 新增 studyLogs 表用于学习时间追踪
    this.version(4).stores({
      cards:
        '&id, contentId, [contentId+contentVersion], contentHash, type, mode, nextReviewDate, [type+mode+nextReviewDate], [topics+nextReviewDate], [unitId+nextReviewDate], [isRelearn+relearnBucket+nextReviewDate], isSoftDeleted, *topics, *tags',
      settings: '&id',
      snapshots: '&id, generatedAt',
      studyLogs: '&id, cardType, startedAt, endedAt, [cardType+startedAt]',
    });
  }
}

export const defaultDb = new MyIELTSDatabase();

const withDefaults = (
  record: Omit<CardRecord, 'createdAt' | 'updatedAt' | 'history'> & { history?: CardHistoryEntry[] },
  now: number
): Omit<CardRecord, 'createdAt' | 'updatedAt'> => {
  return {
    ...record,
    mode: record.mode ?? 'review',
    interval: record.interval ?? DEFAULT_INTERVAL,
    ease: record.ease ?? DEFAULT_EASE,
    consecutiveGood: record.consecutiveGood ?? DEFAULT_GOOD_STREAK,
    history: record.history ?? [],
    isRelearn: record.isRelearn ?? (record.mode === 'relearn'),
  };
};

export const normalizeNewCardRecord = (record: NewCardRecord, now: number = nowTs()): CardRecord => {
  const base = withDefaults(
    {
      id: record.id ?? record.contentId ?? crypto.randomUUID?.() ?? `${record.type}-${now}`,
      type: record.type,
      content: record.content ?? {},
      topics: record.topics ?? [],
      tags: record.tags ?? [],
      unitId: record.unitId,
      sourceId: record.sourceId,
      mode: record.mode ?? 'review',
      nextReviewDate: record.nextReviewDate ?? now,
      interval: record.interval,
      ease: record.ease,
      consecutiveGood: record.consecutiveGood,
      isRelearn: record.isRelearn,
      relearnBucket: record.relearnBucket,
      priority: record.priority,
      isSoftDeleted: record.isSoftDeleted,
      contentId: record.contentId,
      contentVersion: record.contentVersion,
      contentHash: record.contentHash,
      audioKey: record.audioKey,
      audioUrl: record.audioUrl,
      audioBlobRef: record.audioBlobRef,
      speakingMeta: record.speakingMeta,
      writingMeta: record.writingMeta,
      listeningMeta: record.listeningMeta,
      vocabMeta: record.vocabMeta,
      history: record.history,
    },
    now
  );

  return { ...base, createdAt: now, updatedAt: now };
};

export async function createCard(card: NewCardRecord, db: MyIELTSDatabase = defaultDb): Promise<string> {
  const record = normalizeNewCardRecord(card);
  await db.cards.add(record);
  return record.id;
}

export async function getCard(id: string, db: MyIELTSDatabase = defaultDb): Promise<CardRecord | undefined> {
  return db.cards.get(id);
}

export async function updateCard(
  id: string,
  updates: Partial<Omit<CardRecord, 'id' | 'createdAt'>>,
  db: MyIELTSDatabase = defaultDb
): Promise<CardRecord> {
  const existing = await db.cards.get(id);
  if (!existing) {
    throw new Error(`Card ${id} not found`);
  }

  const updatedAt = nowTs();
  const payload: CardRecord = {
    ...existing,
    ...updates,
    updatedAt,
  };

  await db.cards.put(payload);
  return payload;
}

export async function deleteCard(id: string, db: MyIELTSDatabase = defaultDb): Promise<void> {
  await db.cards.delete(id);
}

export function mergeCard(
  existing: CardRecord,
  incoming: NewCardRecord,
  now: number = nowTs(),
  preserveSrs: boolean = true
): CardRecord {
  const current = { ...existing };
  const srsEase = preserveSrs ? current.ease ?? DEFAULT_EASE : incoming.ease ?? current.ease ?? DEFAULT_EASE;
  const srsInterval = preserveSrs ? current.interval ?? DEFAULT_INTERVAL : incoming.interval ?? current.interval ?? DEFAULT_INTERVAL;
  const srsNext = preserveSrs ? current.nextReviewDate ?? now : incoming.nextReviewDate ?? current.nextReviewDate ?? now;
  const srsStreak = preserveSrs
    ? current.consecutiveGood ?? DEFAULT_GOOD_STREAK
    : incoming.consecutiveGood ?? current.consecutiveGood ?? DEFAULT_GOOD_STREAK;
  // mode 是卡片的固有类型属性，应该始终使用种子数据的值（如果提供了的话）
  // 这样当数据源更新卡片模式时（如从 relearn 改为 topic-dictation），能正确更新
  const srsMode = incoming.mode ?? current.mode ?? 'review';
  const srsRelearn = preserveSrs ? current.isRelearn ?? false : incoming.isRelearn ?? current.isRelearn ?? false;
  const srsBucket = preserveSrs ? current.relearnBucket : incoming.relearnBucket ?? current.relearnBucket;
  const srsPriority = preserveSrs ? current.priority : incoming.priority ?? current.priority;
  const srsHistory = preserveSrs ? current.history ?? [] : incoming.history ?? current.history ?? [];

  const updated: CardRecord = {
    ...current,
    ...incoming,
    id: current.id,
    mode: srsMode,
    topics: incoming.topics ?? current.topics ?? [],
    tags: incoming.tags ?? current.tags ?? [],
    unitId: incoming.unitId ?? current.unitId,
    sourceId: incoming.sourceId ?? current.sourceId,
    contentId: incoming.contentId ?? current.contentId,
    contentVersion: incoming.contentVersion ?? current.contentVersion,
    contentHash: incoming.contentHash ?? current.contentHash,
    content: incoming.content ?? current.content ?? {},
    speakingMeta: incoming.speakingMeta ?? current.speakingMeta,
    // writingMeta 需要深度合并，保留用户数据（如 cnTranslation, lastRecall）
    writingMeta: incoming.writingMeta || current.writingMeta
      ? {
          ...current.writingMeta,
          ...incoming.writingMeta,
          // 保留用户产生的数据，不被种子数据覆盖
          cnTranslation: incoming.writingMeta?.cnTranslation ?? current.writingMeta?.cnTranslation,
          lastRecall: current.writingMeta?.lastRecall ?? incoming.writingMeta?.lastRecall,
          bestVersion: current.writingMeta?.bestVersion ?? incoming.writingMeta?.bestVersion,
        }
      : undefined,
    listeningMeta: incoming.listeningMeta ?? current.listeningMeta,
    vocabMeta: incoming.vocabMeta ?? current.vocabMeta,
    audioKey: incoming.audioKey ?? current.audioKey,
    audioUrl: incoming.audioUrl ?? current.audioUrl,
    audioBlobRef: incoming.audioBlobRef ?? current.audioBlobRef,
    isSoftDeleted: incoming.isSoftDeleted ?? current.isSoftDeleted,
    ease: srsEase,
    interval: srsInterval,
    nextReviewDate: srsNext,
    consecutiveGood: srsStreak,
    isRelearn: srsRelearn,
    relearnBucket: srsBucket,
    priority: srsPriority,
    history: srsHistory,
    // 当 preserveSrs=true（种子数据更新）时，保留原有的 updatedAt
    // 这对于同步至关重要：同步逻辑用 updatedAt 判断哪边数据更新
    // 如果种子数据每次都刷新 updatedAt，会导致云端数据无法下载
    updatedAt: preserveSrs ? current.updatedAt : now,
  };
  return updated;
}

export async function upsertCards(
  cards: NewCardRecord[],
  db: MyIELTSDatabase = defaultDb,
  now: number = nowTs(),
  preserveSrs: boolean = true
): Promise<{ inserted: number; updated: number }> {
  let inserted = 0;
  let updated = 0;

  await db.transaction('rw', db.cards, async () => {
    for (const card of cards) {
      const id = card.id ?? card.contentId;
      const existing =
        (id ? await db.cards.get(id) : undefined) ||
        (card.contentId ? await db.cards.where('contentId').equals(card.contentId).first() : undefined);

      if (!existing) {
        await db.cards.add(normalizeNewCardRecord(card, now));
        inserted += 1;
      } else {
        const merged = mergeCard(existing, card, now, preserveSrs);
        await db.cards.put(merged);
        updated += 1;
      }
    }
  });

  return { inserted, updated };
}

export async function saveSettings(
  settings: Partial<SettingsRecord> & { id?: string },
  db: MyIELTSDatabase = defaultDb
): Promise<string> {
  const now = nowTs();
  const existing = settings.id ? await db.settings.get(settings.id) : undefined;
  const id = settings.id ?? 'app';
  const payload: SettingsRecord = {
    id,
    dailyLimits: settings.dailyLimits ?? existing?.dailyLimits ?? {},
    srsPreferences: settings.srsPreferences ?? existing?.srsPreferences,
    studyGoal: settings.studyGoal ?? existing?.studyGoal,
    weeklyGoal: settings.weeklyGoal ?? existing?.weeklyGoal,
    preferences: settings.preferences ?? existing?.preferences,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };

  await db.settings.put(payload);
  return id;
}

export async function getSettings(
  id: string = 'app',
  db: MyIELTSDatabase = defaultDb
): Promise<SettingsRecord | undefined> {
  return db.settings.get(id);
}

export interface DueQuery {
  onOrBefore?: number | Date;
  type?: CardType;
  mode?: CardMode;
  topic?: string;
  unitId?: string;
  limit?: number;
}

const normalizeCutoff = (value?: number | Date) =>
  typeof value === 'number' ? value : value?.getTime() ?? nowTs();

export async function getDueCards(query: DueQuery = {}, db: MyIELTSDatabase = defaultDb): Promise<CardRecord[]> {
  const cutoff = normalizeCutoff(query.onOrBefore);

  let collection = db.cards.where('nextReviewDate').belowOrEqual(cutoff);

  if (query.type && query.mode) {
    collection = db.cards
      .where('[type+mode+nextReviewDate]')
      .between([query.type, query.mode, Dexie.minKey], [query.type, query.mode, cutoff], true, true);
  } else if (query.type) {
    collection = db.cards.where('[type+mode+nextReviewDate]').between(
      [query.type, Dexie.minKey, Dexie.minKey],
      [query.type, Dexie.maxKey, cutoff],
      true,
      true
    );
  }

  const result = await collection.toArray();
  const filtered = result.filter((card) => {
    if (card.nextReviewDate > cutoff) return false;
    if (query.mode && card.mode !== query.mode) return false;
    if (query.topic && !(card.topics ?? []).includes(query.topic)) return false;
    if (query.unitId && card.unitId !== query.unitId) return false;
    return true;
  });

  filtered.sort(
    (a, b) =>
      a.nextReviewDate - b.nextReviewDate ||
      (a.priority ?? 0) - (b.priority ?? 0) ||
      a.id.localeCompare(b.id)
  );

  return query.limit ? filtered.slice(0, query.limit) : filtered;
}

export async function queryDueCardsByTopic(
  topic: string,
  cutoff?: number | Date,
  db: MyIELTSDatabase = defaultDb
): Promise<CardRecord[]> {
  return getDueCards({ onOrBefore: cutoff, topic }, db);
}

export async function queryDueCardsByUnit(
  unitId: string,
  cutoff?: number | Date,
  db: MyIELTSDatabase = defaultDb
): Promise<CardRecord[]> {
  return getDueCards({ onOrBefore: cutoff, unitId }, db);
}

export async function saveSnapshot(snapshot: TodaySnapshot, db: MyIELTSDatabase = defaultDb): Promise<void> {
  await db.snapshots.put(snapshot);
}

export async function getSnapshot(id: string, db: MyIELTSDatabase = defaultDb): Promise<TodaySnapshot | undefined> {
  return db.snapshots.get(id);
}

export async function getLatestSnapshot(db: MyIELTSDatabase = defaultDb): Promise<TodaySnapshot | undefined> {
  return getSnapshot(TODAY_SNAPSHOT_ID, db);
}

export function createIsolatedDatabase(name?: string): MyIELTSDatabase {
  return new MyIELTSDatabase(name);
}

// ============= StudyLogs 学习时间追踪 =============

/** 保存学习记录 */
export async function saveStudyLog(
  log: Omit<StudySessionLog, 'id'> & { id?: string },
  db: MyIELTSDatabase = defaultDb
): Promise<string> {
  const id = log.id ?? crypto.randomUUID?.() ?? `log-${Date.now()}`;
  const record: StudySessionLog = { ...log, id };
  await db.studyLogs.put(record);
  return id;
}

/** 获取指定时间范围内的学习记录 */
export async function getStudyLogs(
  options: {
    startTime?: number;
    endTime?: number;
    cardType?: CardType;
    limit?: number;
  } = {},
  db: MyIELTSDatabase = defaultDb
): Promise<StudySessionLog[]> {
  const { startTime, endTime, cardType, limit } = options;

  let query = db.studyLogs.toCollection();

  if (cardType && startTime) {
    query = db.studyLogs
      .where('[cardType+startedAt]')
      .between([cardType, startTime], [cardType, endTime ?? Dexie.maxKey], true, true);
  } else if (startTime) {
    query = db.studyLogs.where('startedAt').between(startTime, endTime ?? Dexie.maxKey, true, true);
  }

  let results = await query.toArray();

  // 过滤条件
  if (cardType && !startTime) {
    results = results.filter((log) => log.cardType === cardType);
  }
  if (endTime && !startTime) {
    results = results.filter((log) => log.startedAt <= endTime);
  }

  // 排序：最新在前
  results.sort((a, b) => b.startedAt - a.startedAt);

  return limit ? results.slice(0, limit) : results;
}

/** 获取本周开始时间戳（周一 00:00） */
export function getWeekStart(date: Date = new Date()): number {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 周一
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** 获取本周结束时间戳（周日 23:59:59） */
export function getWeekEnd(date: Date = new Date()): number {
  const weekStart = getWeekStart(date);
  return weekStart + 7 * 24 * 60 * 60 * 1000 - 1;
}

/** 获取本周学习统计 */
export async function getWeeklyStudyStats(
  weekStart?: number,
  db: MyIELTSDatabase = defaultDb
): Promise<{
  totalMinutes: number;
  bySkill: Record<CardType, number>;
  cardsCompleted: number;
  logs: StudySessionLog[];
}> {
  const start = weekStart ?? getWeekStart();
  const end = getWeekEnd(new Date(start));

  const logs = await getStudyLogs({ startTime: start, endTime: end }, db);

  const bySkill: Record<CardType, number> = {
    speaking: 0,
    writing: 0,
    listening: 0,
    vocab: 0,
  };

  let totalMs = 0;
  for (const log of logs) {
    totalMs += log.durationMs;
    bySkill[log.cardType] = (bySkill[log.cardType] ?? 0) + Math.round(log.durationMs / 60000);
  }

  return {
    totalMinutes: Math.round(totalMs / 60000),
    bySkill,
    cardsCompleted: logs.length,
    logs,
  };
}

/** 获取今日学习统计 */
export async function getTodayStudyStats(
  db: MyIELTSDatabase = defaultDb
): Promise<{
  totalMinutes: number;
  bySkill: Record<CardType, number>;
  cardsCompleted: number;
}> {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const logs = await getStudyLogs(
    { startTime: todayStart.getTime(), endTime: todayEnd.getTime() },
    db
  );

  const bySkill: Record<CardType, number> = {
    speaking: 0,
    writing: 0,
    listening: 0,
    vocab: 0,
  };

  let totalMs = 0;
  for (const log of logs) {
    totalMs += log.durationMs;
    bySkill[log.cardType] = (bySkill[log.cardType] ?? 0) + Math.round(log.durationMs / 60000);
  }

  return {
    totalMinutes: Math.round(totalMs / 60000),
    bySkill,
    cardsCompleted: logs.length,
  };
}
